import { addDoc, collection, doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { PLATFORM_COLLECTIONS } from './platformSchema';
import { loadRecordsByEquality } from './firestoreQueryHelpers';
import {
  notifyEntitlementGranted,
  notifyInstructorAssignedLesson,
  notifyInstructorAssignedVideo,
} from './notifications';

function dedupeById(items = [], idKey = 'id') {
  const seen = new Set();
  return (items || []).filter((item) => {
    const key = item?.[idKey];
    if (!key || seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

export function getOrganizationInstructors(members = []) {
  return (members || []).filter((member) =>
    ['instructor', 'organization_admin', 'organization_owner'].includes(member.orgRole || member.role)
  );
}

export function getOrganizationStudents(members = [], lessons = []) {
  const memberStudents = (members || []).filter((member) => (member.orgRole || member.role) === 'student');
  const lessonStudents = (lessons || [])
    .map((lesson) => ({
      id: lesson.studentId || lesson.studentEmail || lesson.studentName,
      name: lesson.studentName || lesson.studentEmail || lesson.studentId,
      studentId: lesson.studentId || '',
      studentEmail: lesson.studentEmail || '',
      role: 'student',
    }))
    .filter((student) => student.id);

  return dedupeById([...memberStudents, ...lessonStudents]);
}

export async function assignInstructorToStudent(
  db,
  {
    studentId = '',
    instructorId = '',
    organizationId = '',
  } = {}
) {
  if (!studentId || !instructorId) {
    throw new Error('studentId and instructorId are required');
  }

  await updateDoc(doc(db, 'users', studentId), {
    assignedInstructorId: instructorId,
    organizationId: organizationId || '',
    updatedAt: serverTimestamp(),
  });
}

export async function assignLessonInstanceToInstructor(
  db,
  {
    lessonId = '',
    instructorId = '',
    organizationId = '',
    assignmentSource = 'org_admin',
    actorUserId = '',
    actorName = '',
  } = {}
) {
  if (!lessonId || !instructorId) {
    throw new Error('lessonId and instructorId are required');
  }

  await updateDoc(doc(db, 'lessonInstances', lessonId), {
    assignedInstructorId: instructorId,
    organizationId: organizationId || '',
    assignmentSource,
    updatedAt: serverTimestamp(),
  });

  try {
    await notifyInstructorAssignedLesson(db, {
      instructorId,
      organizationId,
      lessonId,
      actorUserId,
      actorName,
    });
  } catch (error) {
    console.error('Failed to create lesson assignment notification:', error);
  }
}

export async function assignLessonToInstructor(db, payload = {}) {
  return assignLessonInstanceToInstructor(db, payload);
}

export async function assignVideoToInstructor(
  db,
  {
    videoId = '',
    // Pass 72 (§33) — VIDEO assignment is an Auth uid. The canonical param is `instructorAuthUserId`; the legacy
    // `instructorId` param name is accepted for back-compat but callers MUST pass an Auth uid (both assignee
    // fields are set to this value). Never pass a business member id here — resolve it first.
    instructorAuthUserId = '',
    instructorId = '',
    organizationId = '',
    actorUserId = '',
    actorName = '',
  } = {}
) {
  const assigneeAuthUid = instructorAuthUserId || instructorId;
  if (!videoId || !assigneeAuthUid) {
    throw new Error('videoId and instructorAuthUserId are required');
  }

  await updateDoc(doc(db, 'videos', videoId), {
    assignedInstructorId: assigneeAuthUid,
    assignedTo: assigneeAuthUid,
    assigned: true,
    organizationId: organizationId || '',
    updatedAt: serverTimestamp(),
  });

  try {
    await notifyInstructorAssignedVideo(db, {
      instructorId: assigneeAuthUid,
      organizationId,
      videoId,
      actorUserId,
      actorName,
    });
  } catch (error) {
    console.error('Failed to create video assignment notification:', error);
  }
}

export async function inviteOrAddOrganizationInstructor(
  db,
  {
    organizationId = '',
    organizationName = '',
    userId = '',
    email = '',
    name = '',
    role = 'instructor',
  } = {}
) {
  if (!organizationId || (!userId && !email)) {
    throw new Error('organizationId and userId/email are required');
  }

  return addDoc(collection(db, PLATFORM_COLLECTIONS.ORGANIZATION_MEMBERS), {
    organizationId,
    organizationName,
    userId,
    email,
    name,
    role,
    status: userId ? 'active' : 'pending_invite',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function getInstructorAssignedLessons(db, instructorId = '') {
  if (!instructorId) return [];

  const [assignedRecords, legacyRecords] = await Promise.all([
    loadRecordsByEquality(db, {
      collectionName: 'lessonInstances',
      field: 'assignedInstructorId',
      value: instructorId,
    }),
    loadRecordsByEquality(db, {
      collectionName: 'lessonInstances',
      field: 'instructorId',
      value: instructorId,
    }),
  ]);
  const seen = new Set();
  return [...assignedRecords, ...legacyRecords]
    .filter((lesson) => {
      const lessonId = lesson.id || lesson.lessonId;
      if (!lessonId || seen.has(lessonId)) return false;
      seen.add(lessonId);
      return true;
    })
    .map((lesson) => ({ lessonId: lesson.id || lesson.lessonId, ...lesson }));
}

export async function getInstructorAssignedVideos(db, instructorId = '') {
  if (!instructorId) return [];

  const [assignedRecords, legacyRecords] = await Promise.all([
    loadRecordsByEquality(db, {
      collectionName: 'videos',
      field: 'assignedInstructorId',
      value: instructorId,
    }),
    loadRecordsByEquality(db, {
      collectionName: 'videos',
      field: 'assignedTo',
      value: instructorId,
    }),
  ]);
  const seen = new Set();
  return [...assignedRecords, ...legacyRecords].filter((video) => {
    if (!video?.id || seen.has(video.id)) return false;
    seen.add(video.id);
    return true;
  });
}

export async function createContentPack(
  db,
  {
    name = '',
    description = '',
    ownerType = 'platform',
    ownerId = 'platform',
    industry = '',
    disciplines = [],
    isPremium = false,
  } = {}
) {
  return addDoc(collection(db, PLATFORM_COLLECTIONS.CONTENT_PACKS), {
    name,
    description,
    ownerType,
    ownerId,
    industry,
    disciplines,
    isPremium: Boolean(isPremium),
    createdAt: serverTimestamp(),
  });
}

export async function grantOrganizationEntitlement(
  db,
  {
    organizationId = '',
    contentPackId = '',
    status = 'active',
    startDate = null,
    endDate = null,
    sourceType = 'manual_grant',
    sourceId = '',
    billingStatusSnapshot = '',
    grantedByUserId = '',
    grantedReason = '',
    actorUserId = '',
    actorName = '',
  } = {}
) {
  const entitlementRef = await addDoc(collection(db, PLATFORM_COLLECTIONS.ENTITLEMENTS), {
    organizationId,
    contentPackId,
    status,
    startDate,
    endDate,
    sourceType,
    sourceId,
    billingStatusSnapshot,
    grantedByUserId,
    grantedReason,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  try {
    if (status === 'active') {
      await notifyEntitlementGranted(db, {
        organizationId,
        contentPackId,
        actorUserId,
        actorName,
      });
    }
  } catch (error) {
    console.error('Failed to create entitlement notification:', error);
  }

  return entitlementRef;
}
